import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ApiError } from "../../shared/api/http.js";
import { Button, Card, Field, Input } from "../../shared/ui/index.js";
import { CenteredLayout } from "../../shared/ui/CenteredLayout.js";
import { useResetPassword } from "./auth.api.js";
import { rootRoute } from "../../app/root-route.js";

export const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
  component: ResetPasswordScreen,
});

function ResetPasswordScreen() {
  const { token } = resetPasswordRoute.useSearch();
  const navigate = useNavigate();
  const reset = useResetPassword();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm !== "" && confirm !== password;

  if (!token) {
    return (
      <CenteredLayout>
        <Card>
          <p className="text-sm text-stone-600">Lien de réinitialisation invalide.</p>
          <Link
            to="/forgot-password"
            className="mt-3 block text-sm text-stone-500 underline-offset-2 hover:text-stone-800 hover:underline"
          >
            Demander un nouveau lien
          </Link>
        </Card>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <Card>
        <h1 className="font-serif text-xl text-stone-900">Nouveau mot de passe</h1>
        <p className="mt-1 text-sm text-stone-500">Choisissez votre nouveau mot de passe.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (mismatch) return;
            reset.mutate(
              { token, password },
              {
                onSuccess: ({ user }) =>
                  navigate({ to: user.role === "admin" ? "/admin" : "/app" }),
              },
            );
          }}
          className="mt-4 flex flex-col gap-3"
        >
          <Field label="Nouveau mot de passe (8 caractères min.)">
            <Input
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field label="Confirmer le mot de passe">
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>
          {mismatch && (
            <p className="text-sm text-rose-600">Les mots de passe ne correspondent pas.</p>
          )}
          {reset.error && (
            <p className="text-sm text-rose-600">
              {reset.error instanceof ApiError ? reset.error.message : "Erreur"}
            </p>
          )}
          <Button type="submit" disabled={reset.isPending || mismatch}>
            Enregistrer
          </Button>
          {reset.error && (
            <Link
              to="/forgot-password"
              className="text-center text-sm text-stone-500 underline-offset-2 hover:text-stone-800 hover:underline"
            >
              Demander un nouveau lien
            </Link>
          )}
        </form>
      </Card>
    </CenteredLayout>
  );
}
